import { founder, openingHours, pressLogos, pressMentions, salon } from './content'
import { policy } from './policies'
import { giftTiers } from './gifts'
import { codes } from './codes'
import { creators } from './creators'
import { rebuildFaq } from './faq'
import { rebuildLegal } from './legal'
import { rebuildSeo } from './seo'
import { pageDefaults, pages } from './pages'
import { applyFeatures } from './features'
import { safeHref } from '../core/rules/contact'
import { DEFAULT_FEATURES } from '../core/rules/features'
import { upgradePageContent } from '../core/rules/pageContent'
import { withPolicyDefaults } from '../core/rules/policyDefaults'
import { exceptionList, staffList } from '../core/rules/salonDays'
import { salonExceptions, staff } from './staff'
import type { SiteSettings } from '../core/types/site'
import type { PageContent } from '../core/types/pages'

/**
 * De instellingen zoals ze nu lokaal staan — exact de vorm die de backend
 * straks op GET /site levert.
 */
export const localSite = (): SiteSettings => ({
  founder: { ...founder },
  salon: { ...salon },
  openingHours: openingHours.map((d) => ({ ...d })),
  salonExceptions: exceptionList(salonExceptions),
  staff: staffList(staff),
  policy: {
    ...policy,
    consult: { ...policy.consult },
    subscription: {
      ...policy.subscription,
      intervals: [...policy.subscription.intervals],
      pauseOptions: policy.subscription.pauseOptions.map((o) => ({ ...o })),
    },
  },
  giftTiers: giftTiers.map((t) => ({ ...t })),
  codes: codes.map((c) => ({ ...c })),
  creators: creators.map((c) => ({ ...c })),
  press: { logos: pressLogos.map((l) => ({ ...l })), mentions: [...pressMentions] },
  // De vaste standaard, niet de live stand (data/features.ts): het demo-concept hangt aan deze vorm.
  features: { ...DEFAULT_FEATURES },
})

const replaceList = <T,>(target: T[], next: T[]) => target.splice(0, target.length, ...next)

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Zet de instellingen uit de backend klaar, vóórdat de site voor het eerst tekent.
 * Alle componenten lezen dezelfde objecten; alles wat ervan is afgeleid (FAQ,
 * juridische teksten, SEO) wordt hier opnieuw opgebouwd. Na het opstarten
 * veranderen de instellingen niet meer tot de volgende paginalading.
 */
export function applySiteSettings(raw: SiteSettings) {
  // Een oudere of onvolledige server mag de site nooit laten crashen: wat ontbreekt of de
  // verkeerde vorm heeft, blijft op de huidige (lokale) waarde staan.
  const site = (isObj(raw) ? raw : {}) as Partial<SiteSettings>
  if (isObj(site.founder)) Object.assign(founder, site.founder)
  if (isObj(site.salon)) {
    // Links uit de instellingen komen in tientallen `href`s terecht: onveilige waarden
    // (geen https/mailto/tel) vallen terug op de huidige, geldige waarde.
    const links = {
      whatsapp: safeHref(site.salon.whatsapp, salon.whatsapp),
      instagram: safeHref(site.salon.instagram, salon.instagram),
      routeUrl: safeHref(site.salon.routeUrl, salon.routeUrl),
    }
    Object.assign(salon, site.salon, links)
  }
  // Ook binnen `subscription` en `consult` aangevuld (een gedeeltelijk object vervangt dan niet alles).
  Object.assign(policy, withPolicyDefaults(site.policy, localSite().policy))
  if (Array.isArray(site.openingHours)) {
    replaceList(openingHours, site.openingHours)
    // Een server die de salondagen stuurt maar (nog) geen medewerkers of uitzonderingen kent:
    // alleen Sarah en geen uitzonderingen, nooit de voorbeeldcollega uit de lokale data.
    replaceList(staff, staffList(Array.isArray(site.staff) ? site.staff : []))
    replaceList(salonExceptions, exceptionList(Array.isArray(site.salonExceptions) ? site.salonExceptions : []))
  }
  if (Array.isArray(site.giftTiers)) {
    replaceList(
      giftTiers,
      [...site.giftTiers].sort((a, b) => a.threshold - b.threshold),
    )
  }
  if (Array.isArray(site.codes)) replaceList(codes, site.codes)
  if (Array.isArray(site.creators)) replaceList(creators, site.creators)
  if (isObj(site.press)) {
    if (Array.isArray(site.press.logos)) replaceList(pressLogos, site.press.logos)
    if (Array.isArray(site.press.mentions)) replaceList(pressMentions, site.press.mentions)
  }
  applyFeatures(site.features)

  rebuildFaq()
  rebuildLegal()
  rebuildSeo()
}

/**
 * Zet de paginateksten uit de backend klaar. Elke pagina wordt in place bijgewerkt,
 * zodat iedereen die `pages` leest (ook `subscription`) meteen de nieuwe tekst ziet.
 */
export function applyPageContent(raw: PageContent) {
  // Geen object (lege of kapotte serverreactie): de huidige teksten blijven staan.
  if (!isObj(raw)) return
  // Met de standaard erbij: een oudere opslag krijgt nieuwe velden (beelden, Kom langs) meteen.
  const next = upgradePageContent(raw, pageDefaults)
  ;(Object.keys(next) as (keyof PageContent)[]).forEach((key) => {
    const target = pages[key]
    const value = next[key]
    // Optionele delen (zoals `seo`) bestaan lokaal niet altijd: dan gewoon overnemen.
    if (target === undefined || target === null) {
      Object.assign(pages, { [key]: value })
    } else if (Array.isArray(target) && Array.isArray(value)) {
      replaceList(target as unknown[], value as unknown[])
    } else {
      Object.assign(target, value)
    }
  })
}

/**
 * Controleert of de instellingen met elkaar kloppen. Leeg = in orde.
 * De backend hoort dezelfde regels af te dwingen bij opslaan.
 */
export function siteIssues(site: SiteSettings, productExists: (id: string) => boolean): string[] {
  const issues: string[] = []
  const first = [...site.giftTiers].sort((a, b) => a.threshold - b.threshold)[0]
  if (!first || first.threshold !== site.policy.freeShippingFrom) {
    issues.push('De eerste cadeautrede moet gelijk zijn aan de drempel voor gratis verzending')
  }
  site.openingHours.forEach((d) => {
    if (Boolean(d.open) !== Boolean(d.close)) issues.push(`${d.day}: open én sluit invullen, of geen van beide`)
    if (d.open && d.close && d.open >= d.close) issues.push(`${d.day}: sluitingstijd ligt vóór openingstijd`)
  })
  const allCodes = [...site.codes.map((c) => c.code), ...site.creators.map((c) => c.code)]
  const dupes = allCodes.filter((c, i) => allCodes.findIndex((x) => x.toLowerCase() === c.toLowerCase()) !== i)
  if (dupes.length) issues.push(`Dubbele codes: ${dupes.join(', ')}`)
  site.creators.forEach((c) => {
    if (!productExists(c.pickId)) issues.push(`${c.handle}: onbekend product ${c.pickId}`)
  })
  const sub = site.policy.subscription
  if (!sub.intervals.includes(sub.defaultIntervalWeeks)) {
    issues.push(`Het standaardritme (${sub.defaultIntervalWeeks} weken) staat niet tussen de ritmes waaruit klanten kiezen`)
  }
  if (sub.intervals.some((w) => w < 1)) issues.push('Een abonnementsritme moet minstens 1 week zijn')
  if (sub.pauseOptions.length === 0) issues.push('Geef klanten minstens één pauzekeuze')
  if (sub.pauseOptions.some((o) => o.weeks < 1 || !o.label.trim())) issues.push('Elke pauzekeuze heeft een label en minstens 1 week')
  return issues
}
